import { Injectable } from '@angular/core';
import {
  addDays,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  isSameDay,
  endOfWeek,
  startOfWeek,
} from 'date-fns';

export interface DateRange {
  start: Date;
  end: Date;
}

export enum DateRangeId {
  Today = 'today',
  Yesterday = 'yesterday',
  ThisWeek = 'thisWeek',
  LastWeek = 'lastWeek',
  ThisMonth = 'thisMonth',
  LastMonth = 'lastMonth',
  ThisYear = 'thisYear',
  LastYear = 'lastYear',
  Past7 = 'past7',
  Past15 = 'past15',
  Past30 = 'past30',
  Past60 = 'past60',
  Past90 = 'past90',
  Tomorrow = 'tomorrow',
  NextWeek = 'nextWeek',
  NextMonth = 'nextMonth',
  NextYear = 'nextYear',
  Next7 = 'next7',
  Next15 = 'next15',
  Next30 = 'next30',
  Next60 = 'next60',
  Next90 = 'next90',
  Clear = 'clear',
}

export interface PopularDateRange {
  id: DateRangeId;
  name: string;
  category: string;
}

@Injectable({
  providedIn: 'root',
})
export class PopularDateRangesService {
  constructor() {}

  getDateRangeById(id: DateRangeId): DateRange | null {
    const today = startOfDay(new Date());

    switch (id) {
      case DateRangeId.Today:
        return { start: today, end: today };
      case DateRangeId.Yesterday:
        return { start: subDays(today, 1), end: subDays(today, 1) };
      case DateRangeId.ThisWeek:
        return {
          start: subDays(today, today.getDay()),
          end: addDays(today, 6 - today.getDay()),
        };
      case DateRangeId.LastWeek:
        return {
          start: subDays(today, today.getDay() + 7),
          end: subDays(today, today.getDay() + 1),
        };
      case DateRangeId.ThisMonth:
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case DateRangeId.LastMonth:
        return {
          start: startOfMonth(subDays(today, 30)),
          end: endOfMonth(subDays(today, 30)),
        };
      case DateRangeId.ThisYear:
        return {
          start: new Date(today.getFullYear(), 0, 1),
          end: new Date(today.getFullYear(), 11, 31),
        };
      case DateRangeId.LastYear:
        return {
          start: new Date(today.getFullYear() - 1, 0, 1),
          end: new Date(today.getFullYear() - 1, 11, 31),
        };
      case DateRangeId.Past7:
        return { start: subDays(today, 7), end: today };
      case DateRangeId.Past15:
        return { start: subDays(today, 15), end: today };
      case DateRangeId.Past30:
        return { start: subDays(today, 30), end: today };
      case DateRangeId.Past60:
        return { start: subDays(today, 60), end: today };
      case DateRangeId.Past90:
        return { start: subDays(today, 90), end: today };
      case DateRangeId.Tomorrow:
        return { start: addDays(today, 1), end: addDays(today, 1) };
      case DateRangeId.Next7:
        return { start: today, end: addDays(today, 7) };
      case DateRangeId.Next15:
        return { start: today, end: addDays(today, 15) };
      case DateRangeId.Next30:
        return { start: today, end: addDays(today, 30) };
      case DateRangeId.Next60:
        return { start: today, end: addDays(today, 60) };
      case DateRangeId.Next90:
        return { start: today, end: addDays(today, 90) };
      case DateRangeId.NextWeek: // 🟢 Added this case
        return {
          start: startOfWeek(addDays(today, 7), { weekStartsOn: 0 }),
          end: endOfWeek(addDays(today, 7), { weekStartsOn: 0 }),
        };

      case DateRangeId.NextMonth: // 🟢 Added this case
        const startOfNextMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          1
        );
        const endOfNextMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 2,
          0
        );
        return { start: startOfNextMonth, end: endOfNextMonth };
      default:
        return null;
    }
  }

  getPossibleRanges(): PopularDateRange[] {
    return [
      // Current
      { id: DateRangeId.Today, name: 'Today', category: 'Current' },
      { id: DateRangeId.ThisWeek, name: 'This Week', category: 'Current' },
      { id: DateRangeId.ThisMonth, name: 'This Month', category: 'Current' },
      { id: DateRangeId.ThisYear, name: 'This Year', category: 'Current' },

      // Past
      { id: DateRangeId.Yesterday, name: 'Yesterday', category: 'Past' },
      { id: DateRangeId.LastWeek, name: 'Last Week', category: 'Past' },
      { id: DateRangeId.LastMonth, name: 'Last Month', category: 'Past' },
      { id: DateRangeId.LastYear, name: 'Last Year', category: 'Past' },
      { id: DateRangeId.Past7, name: 'Past 7 Days', category: 'Past' },
      { id: DateRangeId.Past15, name: 'Past 15 Days', category: 'Past' },
      { id: DateRangeId.Past30, name: 'Past 30 Days', category: 'Past' },
      { id: DateRangeId.Past60, name: 'Past 60 Days', category: 'Past' },
      { id: DateRangeId.Past90, name: 'Past 90 Days', category: 'Past' },

      // Next (Ensure these are included)
      { id: DateRangeId.Tomorrow, name: 'Tomorrow', category: 'Next' },
      { id: DateRangeId.NextWeek, name: 'Next Week', category: 'Next' },
      { id: DateRangeId.NextMonth, name: 'Next Month', category: 'Next' },
      { id: DateRangeId.NextYear, name: 'Next Year', category: 'Next' },
      { id: DateRangeId.Next7, name: 'Next 7 Days', category: 'Next' },
      { id: DateRangeId.Next15, name: 'Next 15 Days', category: 'Next' },
      { id: DateRangeId.Next30, name: 'Next 30 Days', category: 'Next' },
      { id: DateRangeId.Next60, name: 'Next 60 Days', category: 'Next' },
      { id: DateRangeId.Next90, name: 'Next 90 Days', category: 'Next' },
    ];
  }

  getRangeCategories(): string[] {
    return Array.from(
      new Set(this.getPossibleRanges().map((range) => range.category))
    );
  }

  getRangeName(lowerRange: Date, upperRange: Date): string {
    const today = startOfDay(new Date());

    const ranges = [
      {
        id: DateRangeId.Today,
        range: this.getDateRangeById(DateRangeId.Today),
      },
      {
        id: DateRangeId.ThisWeek,
        range: this.getDateRangeById(DateRangeId.ThisWeek),
      },
      {
        id: DateRangeId.ThisMonth,
        range: this.getDateRangeById(DateRangeId.ThisMonth),
      },
      {
        id: DateRangeId.ThisYear,
        range: this.getDateRangeById(DateRangeId.ThisYear),
      },
      {
        id: DateRangeId.LastWeek,
        range: this.getDateRangeById(DateRangeId.LastWeek),
      },
      {
        id: DateRangeId.LastMonth,
        range: this.getDateRangeById(DateRangeId.LastMonth),
      },
      {
        id: DateRangeId.LastYear,
        range: this.getDateRangeById(DateRangeId.LastYear),
      },
      {
        id: DateRangeId.Yesterday,
        range: this.getDateRangeById(DateRangeId.Yesterday),
      },
      {
        id: DateRangeId.Past7,
        range: this.getDateRangeById(DateRangeId.Past7),
      },
      {
        id: DateRangeId.Past15,
        range: this.getDateRangeById(DateRangeId.Past15),
      },
      {
        id: DateRangeId.Past30,
        range: this.getDateRangeById(DateRangeId.Past30),
      },
      {
        id: DateRangeId.Past60,
        range: this.getDateRangeById(DateRangeId.Past60),
      },
      {
        id: DateRangeId.Past90,
        range: this.getDateRangeById(DateRangeId.Past90),
      },
    ];

    for (const { id, range } of ranges) {
      if (
        range &&
        isSameDay(range.start, lowerRange) &&
        isSameDay(range.end, upperRange)
      ) {
        return this.formatRangeIdToName(id);
      }
    }

    return '';
  }

  private formatRangeIdToName(id: DateRangeId): string {
    const rangeNames: Record<DateRangeId, string> = {
      [DateRangeId.Today]: 'Today',
      [DateRangeId.Yesterday]: 'Yesterday',
      [DateRangeId.ThisWeek]: 'This Week',
      [DateRangeId.ThisMonth]: 'This Month',
      [DateRangeId.ThisYear]: 'This Year',
      [DateRangeId.LastWeek]: 'Last Week',
      [DateRangeId.LastMonth]: 'Last Month',
      [DateRangeId.LastYear]: 'Last Year',
      [DateRangeId.Past7]: 'Past 7 Days',
      [DateRangeId.Past15]: 'Past 15 Days',
      [DateRangeId.Past30]: 'Past 30 Days',
      [DateRangeId.Past60]: 'Past 60 Days',
      [DateRangeId.Past90]: 'Past 90 Days',
      [DateRangeId.Tomorrow]: 'Tomorrow',
      [DateRangeId.NextWeek]: 'Next Week',
      [DateRangeId.NextMonth]: 'Next Month',
      [DateRangeId.NextYear]: 'Next Year',
      [DateRangeId.Next7]: 'Next 7 Days',
      [DateRangeId.Next15]: 'Next 15 Days',
      [DateRangeId.Next30]: 'Next 30 Days',
      [DateRangeId.Next60]: 'Next 60 Days',
      [DateRangeId.Next90]: 'Next 90 Days',
      [DateRangeId.Clear]: 'Clear',
    };

    return rangeNames[id] ?? 'Unknown';
  }
}
